import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { get as _get } from 'lodash';
import { makeStyles, useTheme } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionActions from '@mui/material/AccordionActions';

import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '@mui/icons-material/Help';
import LockIcon from '@mui/icons-material/Lock';

import MultiGauge from '../components/charts/multigauge';
import SparklineGroup from '../components/charts/sparklinegroup';
import DataNode from '../components/cluster/datanode';
import Indices from '../components/cluster/indices';
import ConfirmationDialog from '../components/confirmationdlg';
import SearchBar from '../components/searchbar';
import Tooltip from '../components/styled/tooltip';
import Validator, { useValidate, checkValidity } from '../components/validator';

import ClusterService from '../data/cluster-service';
import HealthService, { DocsTipFilter } from '../data/health-service';
import Auth from '../data/auth-service';
import DataContext from '../data/data-context';

import Helpers from '../libraries/helpers';
import Moment from 'moment/moment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogIcon from '@mui/icons-material/InsertDriveFile';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

const useStyles = makeStyles(theme => ({
    // buttonRight: {
    //     float: 'right',
    //     color: theme.palette.common.white,
    //     marginTop: theme.spacing(2),
    //     marginRight: theme.spacing(2),
    //     paddingTop: theme.spacing(1),
    //     paddingBottom: theme.spacing(1),
    // },
    centerViz: {
        display: 'block',
        margin: '0 auto',
    },
    fullWidth: {
        width: '100%',
    },
    textCenter: {
        textAlign: 'center',
    },
    totalGauges: {
        '& svg': {
            marginRight: 'auto',
            marginLeft: 'auto',
            display: 'block',
        }
    },
    card: {
        position: 'absolute',
        width: 500,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    openAccordion: {
        display: 'none',
    },
    dialogHighlight: {
        color: theme.palette.secondary.main,
        marginTop: theme.spacing(2),
        textAlign: 'center',
    },
    errorPanel: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.error.main}`,
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    warnMsg: {
        border: `1px solid ${theme.palette.warning.main} !important`,
    },
    infoMsg: {
        border: `1px solid ${theme.palette.info.main} !important`,
    },
    registrationItem: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        fontSize: '1rem',
        cursor: 'pointer',
        borderRadius: '25px',
        marginTop: '-1px',
        marginBottom: '8px',
        marginLeft: '16px',
        paddingLeft: '10px',
        paddingRight: '10px',
    },
    registrationItemAction: {
        background: theme.palette.primary.main,
    },
    registrationItemWarn: {
        background: theme.palette.warning.main,
    },
    clusterStatus: {
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        borderRadius: '3px',
        padding: '3px 15px',
    },
    clusterWarning: {
        background: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
    },
    clusterCritical: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
    clusterInfo: {
        background: theme.palette.info.main,
        color: theme.palette.info.contrastText,
    },
    online: {
        height: '30px',
        marginLeft: '10px',
        padding: '5px 8px',
        borderRadius: '5px',
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    },
    offline: {
        height: '30px',
        marginLeft: '10px',
        padding: '5px 8px',
        borderRadius: '5px',
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
    panelHeading: {
        ...theme.typography.button,
        paddingLeft: theme.spacing(1),
    },
    panelDetails: {
        paddingTop: 0,
        marginTop: -15,
    },
    sensorLogs: {
        maxHeight: '300px',
        overflowY: 'auto',
        overflowWrap: 'anywhere',
    },
    logBox: {
        background: theme.styled.paper.background,
        border: `1px solid ${theme.palette.primary.main}`,
        width: '100%',
        minHeight: '200px',
    },
}));

const sparklineSettings = {
    margin: 10,
    labelWidth: 120,
    chartHeight: 50,
    scale: 'linear',
    vgrid: true,
};

const MetadataTab = 0;
const AnomaliesTab = 1;

const Cluster = () => {
    const theme = useTheme();

    const classes = useStyles();
    const { systemHealth, setSystemHealth } = useContext(DataContext);

    const [deleteIndexOpen, setDeleteIndexOpen] = useState(false);
    const [deleteDataNodeOpen, setDeleteDataNodeOpen] = useState(false);
    const [reregisterOpen, setReregisterOpen] = useState(false);
    const [dataNodeOpen, setDataNodeOpen] = useState(false);
    const [metadataDB, setMetadataDB] = useState({});
    const [dataNodes, setDataNodes] = useState({});
    const [indices, setIndices] = useState({});
    const [selectedIndex, setSelectedIndex] = useState('');
    const [selectedDataNode, setSelectedDataNode] = useState(null);
    const [selectedRoleNode, setSelectedRoleNode] = useState(null);
    const [newDataNodeDetails, setNewDataNodeDetails] = useState({
        host: '',
        password: '',
    });
    const [needReregistration, setNeedReregistration] = useState([]);
    const [enableDisableOpen, setEnableDisableOpen] = useState(false);
    const [elasticFailed, setElasticFailed] = useState(false);
    const [searchDatanodes, setSearchDatanodes] = useState('');
    const [searchIndices, setSearchIndices] = useState('');
    const [wantPassword, setWantPassword] = useState('');
    const [wantMigration, setWantMigration] = useState();
    const [device, setDevice] = useState();
    const datanodeForm = useValidate();
    const [resetAddDataNodeFields, setResetAddDataNodeFields] = useState(false);
    const [tabValue, setTabValue] = useState(MetadataTab);
    const clusterPerms = Auth.permissions.get('Cluster');
    const sensorPerms = Auth.permissions.get('Sensors');
    const cmcPerms = Auth.permissions.get('CMC');
    const healthPerms = Auth.permissions.get('SystemHealth');
    const mtdtIngestPerms = Auth.permissions.get('MetadataIngest');

    const MC2Check = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    const [healthHistory, setHealthHistory] = useState({});

    useEffect(() => {
        if (systemHealth && _get(systemHealth, 'object.health_metrics.metadata_db')) {
            setDevice(systemHealth.object.health_metrics.device_uuid);
            // hide indices starting with '.' and adjust counts accordingly
            const totals = _get(systemHealth, 'object.health_metrics.metadata_db.totals', {});
            const indices = Object.entries(_get(systemHealth, 'object.health_metrics.metadata_db.indices', {}))
                .filter(([k, v]) => {
                    if (k.startsWith('.') && totals[`number_of_indices_${v.status}`]) {
                        totals[`number_of_indices_${v.status}`]--;
                    }
                    return !k.startsWith('.');
                })
                .reduce((a, [k, v]) => ({ ...a, [k]: v }), {});

            setMetadataDB({
                ...systemHealth.object.health_metrics.metadata_db,
                totals,
                indices
            });
            setElasticFailed(false);
        } else if (_get(systemHealth, 'object.health_metrics') && !_get(systemHealth, 'object.health_metrics.metadata_db')) {
            setElasticFailed(true);
        }
        setWantPassword(_get(systemHealth, 'set_es_password'));
        setWantMigration(_get(systemHealth, 'migration_status'));
    }, [systemHealth]);

    useEffect(() => {
        if (!device) return;

        if (MC2Check ? cmcPerms.read : sensorPerms.read) {
            HealthService.getHealthHistory(device).then((data) => {
                const history = (data.history || []).sort((a, b) =>
                    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                setHealthHistory({
                    start: history.length > 0 ? history[0].timestamp : undefined,
                    end: history.length > 0
                        ? history[history.length - 1].timestamp : undefined,

                    // in this case there's only one group with one sparkline
                    metrics: [{
                        index: 0,
                        timeline: history,
                        key: 'metadata',
                        metrics: [{
                            key: 'avg_number_of_docs',
                            type: 'line',
                            title: 'Avg. Doc Count',
                            value: d => _get(d, 'metadata.avg_number_of_docs'),
                            tipFormat: DocsTipFilter,
                        }]
                    }]
                });
            });
        }
    }, [device]);

    useEffect(() => {
        if (metadataDB.nodes) {
            const s = Object.keys(metadataDB.nodes).reduce((a, c) => {
                if (metadataDB.nodes[c].ip.includes(searchDatanodes) || metadataDB.nodes[c].bricata_model.toLowerCase().includes(searchDatanodes.toLowerCase())) {
                    if (!Object.prototype.hasOwnProperty.call(a, metadataDB.nodes[c].bricata_uuid)) {
                        a[metadataDB.nodes[c].bricata_uuid] = [];
                    }
                    a[metadataDB.nodes[c].bricata_uuid].push(metadataDB.nodes[c]);
                }
                return a;
            }, {});
            setDataNodes(s);
            setNeedReregistration(Object.keys(metadataDB.nodes).reduce((a, c) => {
                if (metadataDB.nodes[c].needs_reregister) {
                    if (!a.filter(v => v.bricata_uuid === metadataDB.nodes[c].bricata_uuid).length) {
                        a.push(metadataDB.nodes[c]);
                    }
                }
                return a;
            }, []));
        }
        if (metadataDB.indices) {
            const s = Object.keys(metadataDB.indices).reduce((a, c) => {
                if (c.includes(searchIndices)) {
                    a[c] = metadataDB.indices[c];
                }
                return a;
            }, {});
            setIndices(s);
        }
    }, [metadataDB]);

    useEffect(() => {
        if (metadataDB && metadataDB.nodes) {
            const s = Object.keys(metadataDB.nodes).reduce((a, c) => {
                if (metadataDB.nodes[c].ip.includes(searchDatanodes) || metadataDB.nodes[c].bricata_model.toLowerCase().includes(searchDatanodes.toLowerCase())) {
                    if (!Object.prototype.hasOwnProperty.call(a, metadataDB.nodes[c].bricata_uuid)) {
                        a[metadataDB.nodes[c].bricata_uuid] = [];
                    }
                    a[metadataDB.nodes[c].bricata_uuid].push(metadataDB.nodes[c]);
                }
                return a;
            }, {});
            setDataNodes(s);
        }
    }, [searchDatanodes]);

    useEffect(() => {
        if (metadataDB && metadataDB.indices) {
            const s = Object.keys(metadataDB.indices).reduce((a, c) => {
                if (c.includes(searchIndices)) {
                    a[c] = metadataDB.indices[c];
                }
                return a;
            }, {});
            setIndices(s);
        }
    }, [searchIndices]);

    const buildCharts = () => {
        if (!metadataDB || !metadataDB.totals) return [];
        const x = metadataDB.totals;
        const offlineNodes = Object.keys(metadataDB.nodes).reduce((a, c) => {
            if (Object.prototype.hasOwnProperty.call(metadataDB.nodes[c], 'offline') && metadataDB.nodes[c].offline) {
                a++;
            }
            return a;
        }, 0);

        return [
            { title: 'CPU', val: x.cpu_usage * 100 },
            { title: 'RAM', val: x.ram_usage * 100 },
            { title: 'Disk', val: x.disk_usage * 100 },
            { title: 'Shards', val: x.active_shards_percent_as_number, severities: { red: 100 } },
            { title: 'Nodes', val: ((Object.keys(metadataDB.nodes).length - offlineNodes) / Object.keys(metadataDB.nodes).length) * 100, severities: { red: 100 } },
            {
                val: x.number_of_indices_green,
                title: 'Indices',
                data: [
                    { key: 'green', value: x.number_of_indices_green || 0 },
                    { key: 'yellow', value: x.number_of_indices_yellow || 0 },
                    { key: 'red', value: x.number_of_indices_red || 0 },
                    { key: 'unknown', value: x.number_of_indices_unknown || 0 },
                ]
            },
        ];
    };

    const getHealth = () => {
        if (healthPerms.read) {
            HealthService.getSystemHealth().then(res => {
                // defense against the dark arts of permissions
                setSystemHealth({ ...res, object: res.object || {} });
            });
        }
    };

    const handleAddDataNode = () => {
        setDataNodeOpen(false);
        ClusterService.addNode(newDataNodeDetails).then(() => {
            getHealth();
            handleDataNodeCancel();
            setResetAddDataNodeFields(true);

            toast.success('Data node added successfully. It should appear within the list momentarily');
        }, err => {
            toast.error(err);
        });
    };

    const handleNewDataNodeUpdate = (e, validID, valid) => {
        datanodeForm.set({ ...datanodeForm.get(), [validID]: valid });
        if (resetAddDataNodeFields) {
            setResetAddDataNodeFields(false);
        }
        setNewDataNodeDetails({
            ...newDataNodeDetails,
            [e.target.name]: e.target.value,
        });
    };

    const handleDataNodeOpen = () => {
        setDataNodeOpen(!dataNodeOpen);
    };

    const handleStartMigration = () => {
        toast.info("Starting migration. This may be a long process. You will be notified when it's complete.");
        ClusterService.startMigration().then(() => {
            console.log('Migration started successfully');
            setWantMigration(current => current === 'done' ? current : 'running');
        }, err => {
            console.log('Migration start failed', err);
            setWantMigration(current => current === 'done' ? current : 'manual');
        });
    };

    const handleMigrationAck = () => {
        setWantMigration('done');
        ClusterService.ackMigration();
    };

    const handleDataNodeCancel = () => {
        setNewDataNodeDetails({ host: '', password: '' });
        setResetAddDataNodeFields(true);
        setDataNodeOpen(false);
    };

    const handleOpenDeleteIndex = (index) => {
        setSelectedIndex(index[0]);
        setDeleteIndexOpen(true);
    };

    const handleCloseDeleteIndex = () => {
        setDeleteIndexOpen(false);
        setSelectedIndex('');
    };

    const handleOpenDeleteDataNode = (node) => {
        setSelectedDataNode(node);
        setDeleteDataNodeOpen(true);
    };

    const handleCloseDeleteDataNode = () => {
        setSelectedDataNode(null);
        setDeleteDataNodeOpen(false);
    };

    const handleOpenReregister = (node) => {
        setSelectedDataNode(node);
        setReregisterOpen(true);
    };

    const handleCloseReregister = () => {
        setSelectedDataNode(null);
        setReregisterOpen(false);
    };

    const handleDeleteIndex = () => {
        setDeleteIndexOpen(false);
        ClusterService.deleteIndex(selectedIndex).then((res) => {
            toast.success('Deleted index successfully');
            getHealth();
        }, err => {
            toast.error(err);
        });
    };

    const handleReregisterDataNode = () => {
        setReregisterOpen(false);
        ClusterService.reregisterNode(selectedDataNode.ip).then((res) => {
            toast.success('Reregistration process has begun. Please wait until the process has completed to perform other actions.');
            getHealth();
        }, err => {
            toast.error(err);
        });
    };

    const handleDeleteDataNode = () => {
        setDeleteDataNodeOpen(false);
        ClusterService.deleteNode(selectedDataNode.ip).then((res) => {
            getHealth();
            toast.success('Data node deleted successfully. It should be removed from the list momentarily');
        }, err => {
            toast.error(err);
        });
    };

    const handleDataNodeSearch = ({ search }) => {
        setSearchDatanodes(search);
    };

    const handleIndexSearch = ({ search }) => {
        setSearchIndices(search);
    };

    const handleOpenEnableDisableRole = (role, node) => {
        setSelectedRoleNode({ role, node });
        setEnableDisableOpen(true);
    };

    const handleCloseEnableDisableRole = () => {
        setEnableDisableOpen(false);
    };

    const handleEnableDisableRole = () => {
        ClusterService.setNodeRole(
            selectedRoleNode.node.id,
            selectedRoleNode.node.bricata_instance,
            selectedRoleNode.role,
            selectedRoleNode.role === 'cluster_manager' ? !selectedRoleNode.node.cluster_manager_node : !selectedRoleNode.node.data_node
        ).then(() => {
            toast.success('Data node role updated successfully');
            getHealth();
            setEnableDisableOpen(false);
        }, err => {
            toast.error(err);
            setEnableDisableOpen(false);
        });
    };

    const handleTabChange = (e, tab) => {
        setTabValue(tab);
    };

    // a change to decorate health events
    const decorate = useCallback((id, msg) => {
        switch (id) {
            case 'openeseach_no_health_data':
            case 'elasticsearch_no_health_data':
                const tdata = Moment(_get(systemHealth, 'object.health_metrics.metadata_db.timestamp'));
                return `${msg} since ${tdata.format(theme.date.short)}`;
            default:
                return msg;
        }
    }, [systemHealth]);

    return (
        <React.Fragment>
            <Box className={classes.errorPanel} display={elasticFailed ? 'block' : 'none'}>
                Failed to get Opensearch cluster health
            </Box>
            {(_get(systemHealth, 'object.health_metrics.issues', []) || [])
                .filter(v => (v.id.startsWith('opensearch_') || v.id.startsWith('elasticsearch_')))
                // don't show two alerts for reregistration error
                .filter(v => v.id !== 'opensearch_mismatched_cluster_managers' &&
                             v.id !== 'elasticsearch_mismatched_cluster_managers' &&
                             !needReregistration.length)
                .map((v, i) => (
                    <Box key={'error-' + i} className={classes.errorPanel} display={systemHealth && systemHealth.object.health_status.status === 'critical' ? 'block' : 'none'}>
                        {decorate(v.id, v.message)}
                    </Box>
                ))}
            {clusterPerms.write && <Box className={classes.errorPanel} display={needReregistration.length ? 'flex' : 'none'}>
                Opensearch nodes need re-registration: {needReregistration.map((v, i) => {
                    return (
                        <Box
                            key={'reregister-list-item-' + i}
                            className={classes.registrationItem}
                            onClick={() => { handleOpenReregister(v) }}
                        >
                            {v.ip}
                        </Box>
                    );
                })}
            </Box>}

            {mtdtIngestPerms.read && wantMigration !== 'done' &&
              <MigrationStatus
                  wantMigration={wantMigration}
                  onMigrationStatusAck={handleMigrationAck}
                  onStartMigration={handleStartMigration}/>
            }

            <Grid container spacing={3} style={{ marginBottom: '32px' }}>
                <Grid item xs={12} container justifyContent="space-between">
                    <Box pt={2} className={clsx({
                        [classes.clusterStatus]: true,
                        [classes.clusterCritical]: !!(metadataDB.totals && metadataDB.totals.status === 'red'),
                        [classes.clusterWarning]: !!(metadataDB.totals && metadataDB.totals.status === 'yellow'),
                    })}>
                        <Typography variant="h6">
                            <Box>{metadataDB.totals ? metadataDB.totals.cluster_name : null}</Box>
                        </Typography>
                    </Box>

                    <Box>
                        {clusterPerms.write &&
                          <Box component="span">
                              <Button
                                  variant="contained"
                                  color="primary"
                                  aria-label="add new data node"
                                  onClick={handleDataNodeOpen}>
                                  <AddIcon /> Data Node
                              </Button>
                          </Box>
                        }
                    </Box>
                </Grid>

                <Grid container item justifyContent="flex-end">
                    <Grid item xs={6}>
                        <Box mb={2}>
                            <Accordion expanded={dataNodeOpen}>
                                <AccordionSummary className={classes.openAccordion}></AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="h5" component="h5">Add Data Node</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Validator
                                                valueType="host"
                                                errorText="Host/IP Address is invalid"
                                                resetField={resetAddDataNodeFields}
                                                fullWidth
                                                type="text"
                                                label="Hostname or IP Address"
                                                name="host"
                                                value={newDataNodeDetails.host}
                                                onChange={handleNewDataNodeUpdate}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Validator
                                                fullWidth
                                                errorText="Password cannot be empty"
                                                resetField={resetAddDataNodeFields}
                                                type="password"
                                                label="Password"
                                                name="password"
                                                value={newDataNodeDetails.password}
                                                onChange={handleNewDataNodeUpdate}
                                            />
                                        </Grid>
                                        <Grid item xs={12} container justifyContent="flex-end">
                                            <Box mr={2}>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    aria-label="cancel"
                                                    onClick={handleDataNodeCancel}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                            <Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    aria-label="Add"
                                                    onClick={handleAddDataNode}
                                                    disabled={!checkValidity(datanodeForm.get())}
                                                >
                                                    Add
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                    <Grid container justifyContent="space-evenly">
                        {buildCharts().map((v, i) => {
                            return (<Grid item xs={2} key={`${v.title}-${i}`}>
                                <MultiGauge
                                    className={classes.totalGauges}
                                    label={v.title}
                                    value={v.val}
                                    data={v.data}
                                    severities={v.severities}
                                    small={v.small}
                                    size={v.size}
                                    colors={v.colors} />
                            </Grid>
                            );
                        })}
                    </Grid>
                    <Grid container justifyContent="space-evenly" className={classes.textCenter}>
                        <Grid item xs={2}>Total Cores:      {metadataDB.totals ? metadataDB.totals.allocated_cpus : 0}</Grid>
                        <Grid item xs={2}>
                            RAM: {metadataDB.totals
                                ? Helpers.formatBytes(metadataDB.totals.total_ram) : 0}
                            {/* https://github.com/elastic/elasticsearch/issues/27157 */}
                            <Tooltip title='Used memory is calculated as the total physical memory on the system minus the free physical memory on the system. The free physical memory on the system is ultimately obtained from sysconf(_SC_AVPHYS_PAGES) on Linux which includes only the pages the OS can allocate without flushing the cache or paging to disk.'>
                                <span style={{ fontSize: 16, paddingLeft: 7 }}>
                                    <HelpIcon color="secondary" style={{ fontSize: 16 }}/>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={2}>Total:       {metadataDB.totals ? Helpers.formatBytes(metadataDB.totals.total_disk) : 0}</Grid>
                        <Grid item xs={2}>Active Shards:    {metadataDB.totals ? metadataDB.totals.active_shards : 0}</Grid>
                        <Grid item xs={2}>Total Nodes:      {metadataDB.nodes ? Object.keys(metadataDB.nodes).length : 0}</Grid>
                        <Grid item xs={2}>Total Indices:    {metadataDB.totals ? Object.keys(metadataDB.indices).length : 0}</Grid>
                    </Grid>
                </Grid>
                {healthHistory.metrics && <Grid item xs={12}>
                    <SparklineGroup
                        metrics={healthHistory.metrics}
                        height={sparklineSettings.chartHeight + sparklineSettings.margin}
                        start={healthHistory.start}
                        end={healthHistory.end}
                        settings={sparklineSettings}
                        key={'cluster'}>
                    </SparklineGroup>
                </Grid>}
                <Grid container item xs={12}>
                    <Card className={classes.fullWidth}>
                        <Box pt={2} px={2} display="flex" justifyContent="space-between">
                            <Box display="flex">
                                <Typography variant="h6">Nodes -</Typography>
                                {metadataDB.nodes && (() => {
                                    let online = 0;
                                    let offline = 0;

                                    Object.keys(metadataDB.nodes).forEach((v) => {
                                        if (Object.prototype.hasOwnProperty.call(metadataDB.nodes[v], 'offline') && metadataDB.nodes[v].offline) {
                                            offline++;
                                            return;
                                        }
                                        online++;
                                    });

                                    const on = online ? <Box className={classes.online}>{online} Online</Box> : null;
                                    const off = offline ? <Box className={classes.offline}>{offline} Offline</Box> : null;

                                    return (
                                        <Box display="flex">
                                            {on}
                                            {off}
                                        </Box>
                                    );
                                })()}
                            </Box>
                            <Box>
                                <SearchBar immediate onSearch={handleDataNodeSearch} searchservice={{
                                    ...ClusterService,
                                    searchPlaceholder: 'Search data nodes',
                                }} />
                            </Box>
                        </Box>
                        <Box p={2}>
                            <Grid container item xs={12} spacing={3}>
                                {dataNodes ? Object.keys(dataNodes).map((v) => {
                                    return (
                                        <DataNode
                                            key={`datanode-${v}`}
                                            nodeData={dataNodes[v]}
                                            onRoleChange={handleOpenEnableDisableRole}
                                            onReregister={handleOpenReregister}
                                            onDelete={handleOpenDeleteDataNode}
                                            deletable={(() => {
                                                if (metadataDB.datanodes && metadataDB.datanodes[v]) {
                                                    return metadataDB.datanodes[v].allow_delete;
                                                }
                                                return false;
                                            })()}
                                            status={metadataDB.totals.status}
                                        />
                                    );
                                }) : null}
                            </Grid>
                        </Box>
                    </Card>
                </Grid>
                <Grid container item xs={12}>
                    <AppBar position="static">
                        <Tabs value={tabValue} onChange={handleTabChange}>
                            {<Tab aria-label="metadata tab" label="Metadata" />}
                            {<Tab aria-label="anomalies tab" label="Anomalies" />}
                        </Tabs>
                    </AppBar>
                        <Card className={classes.fullWidth}>
                            <Box p={2} value={tabValue} index={MetadataTab} hidden={tabValue !== MetadataTab}>
                                <Box pt={2} px={2}>
                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="h6">
                                                    Indices {indices ? Object.entries(indices).filter(x => x.length && x[0].includes('bricata_cmc')).length : 0}
                                                </Typography>
                                            </Box>
                                            <Box><SearchBar immediate onSearch={handleIndexSearch} searchservice={ClusterService} /></Box>
                                        </Box>
                                    </Grid>
                                </Box>
                                <Box p={2}>
                                    <Grid container item xs={12} spacing={3}>
                                        {indices ? Object.entries(indices).filter(x => x.length && x[0].includes('bricata_cmc')).map((v) => {
                                            return (<Indices key={`indices-${v[0]}`} indexID={v[0]} indexData={v[1]} onDelete={() => { handleOpenDeleteIndex(v) }} />);
                                        }) : null}
                                    </Grid>
                                </Box>
                            </Box>
                            <Box p={2} value={tabValue} index={AnomaliesTab} hidden={tabValue !== AnomaliesTab}>
                                <Box pt={2} px={2}>
                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="h6">
                                                    Indices {indices ? Object.entries(indices).filter(x => x.length && x[0].includes('opensearch-ad-plugin')).length : 0}
                                                </Typography>
                                            </Box>
                                            <Box><SearchBar immediate onSearch={handleIndexSearch} searchservice={ClusterService} /></Box>
                                        </Box>
                                    </Grid>
                                </Box>
                                <Box p={2}>
                                    <Grid container item xs={12} spacing={3}>
                                        {indices ? Object.entries(indices).filter(x => x.length && x[0].includes('opensearch-ad-plugin')).map((v) => {
                                            return (<Indices key={`indices-${v[0]}`} indexID={v[0]} indexData={v[1]} onDelete={() => { handleOpenDeleteIndex(v) }} />);
                                        }) : null}
                                    </Grid>
                                </Box>
                            </Box>
                        </Card>
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteIndex}
                onOK={handleDeleteIndex}
                open={deleteIndexOpen}
                message={(() => {
                    return (
                        <Box>
                            Are you sure you would like to delete the index?
                            <Box className={classes.dialogHighlight}>{selectedIndex}</Box>
                        </Box>
                    );
                })()}
                title="Delete index"
                cancelLabel="cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteDataNode}
                onOK={handleDeleteDataNode}
                open={deleteDataNodeOpen}
                message={(() => {
                    return (
                        <Box>
                            Are you sure you would like to delete the data node?
                            <Box className={classes.dialogHighlight}>{selectedDataNode && selectedDataNode.ip}</Box>
                        </Box>
                    );
                })()}
                title="Delete Data Node"
                cancelLabel="cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseReregister}
                onOK={handleReregisterDataNode}
                open={reregisterOpen}
                message={(() => {
                    return (
                        <Box>
                            Are you sure you would like to reregister the appliance?
                            <Box>
                                This operation applies to all nodes on
                                <Box className={classes.dialogHighlight} display="inline-block" mx={1}>
                                    {selectedDataNode && selectedDataNode.ip}.
                                </Box>
                                It can take up to a few minutes to complete and will run in the background. Please
                                wait for this operation to complete before performing any other actions.
                            </Box>
                        </Box>
                    );
                })()}
                title="Reregister Data Node"
                cancelLabel="cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseEnableDisableRole}
                onOK={handleEnableDisableRole}
                open={enableDisableOpen}
                message={(() => {
                    let roleContext = 'Enable';
                    if (selectedRoleNode) {
                        switch (selectedRoleNode.role) {
                            case 'cluster_manager':
                                roleContext = selectedRoleNode.node.cluster_manager_node ? 'Disable' : 'Enable';
                                break;
                            case 'data':
                                roleContext = selectedRoleNode.node.data_node ? 'Disable' : 'Enable';
                                break;
                            case 'ingest' :
                                roleContext = selectedRoleNode.node.ingest_node ? 'Disable' : 'Enable';
                                break;
                        }
                    }
                    return (
                        <Box>
                            {`${roleContext} the '${selectedRoleNode && selectedRoleNode.role}' role on this node?`}
                            <Box>
                                It can take up to a few minutes to complete and will run in the background. Please
                                wait for this operation to complete before performing any other actions.
                            </Box>
                        </Box>
                    );
                })()}
                title="Update Data Node Role"
                cancelLabel="cancel"
                okLabel="Yes"
            />
        </React.Fragment>
    );
};

const MigrationStatus = (props) => {
    const { wantMigration, onMigrationStatusAck, onStartMigration } = props;
    const classes = useStyles();
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [logs, setLogs] = useState();
    const mtdtIngestPerms = Auth.permissions.get('MetadataIngest');

    const handleFetchLogs = useCallback(() => {
        ClusterService.getMigrationLog().then((data) => {
            setLogs((data || []).map(log => ({ ...log, timestamp: Moment(log.timestamp) })));
        });
    }, []);

    const needMigration = useCallback(() => {
        return wantMigration === 'manual' || wantMigration === 'failed' || wantMigration === 'running';
    }, [wantMigration]);

    const getMigrationMsg = useCallback(() => {
        switch (wantMigration) {
            case 'running':
                return 'Data Migration from ElasticSearch to Opensearch is running';
            case 'manual':
                return 'Data Migration from ElasticSearch to Opensearch is required';
            case 'failed':
                return 'Data Migration from ElasticSearch to Opensearch failed';
            case 'success':
                return 'Data Migration from ElasticSearch to Opensearch was successful';
            default:
                return `Data Migration from ElasticSearch to Opensearch status is ${wantMigration}`;
        }
    }, [wantMigration]);

    useEffect(() => {
        ClusterService.getMigrationLog().then((data) => {
            setLogs((data || []).map(log => ({ ...log, timestamp: Moment(log.timestamp) })));
        });
    }, []);

    return wantMigration ? <React.Fragment>
        <Box className={clsx({
            [classes.infoMsg]: wantMigration === 'running' || wantMigration === 'success',
            [classes.warnMsg]: wantMigration === 'manual',
            [classes.errorPanel]: true,
        })} display='flex'>
            {getMigrationMsg()}
            { wantMigration === 'success' && mtdtIngestPerms.override &&
                <Box className={clsx(classes.registrationItemAction, classes.registrationItem)} onClick={onMigrationStatusAck}>
                    {"Don't show this again"}
                </Box>
            }
            { needMigration() && wantMigration !== 'running' && mtdtIngestPerms.override &&
                <Box className={clsx(classes.registrationItemAction, classes.registrationItem)} onClick={onStartMigration}>
                    {'Start Migration'}
                </Box>
            }
        </Box>
        <Box display={'block'} style={{ marginBottom: 25 }}>
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <LogIcon />
                    <Typography className={classes.panelHeading}>Migration Logs</Typography>
                </AccordionSummary>
                <AccordionActions>
                    <Button onClick={handleFetchLogs}>Refresh</Button>
                </AccordionActions>
                <AccordionDetails className={classes.panelDetails}>
                    <Box width="100%">
                        <Box className={classes.logBox}>
                            <List className={classes.sensorLogs}>
                                {logs && (!logs.length) && <ListItem>
                                    <Box width="100%">
                                        <Box>Click refresh to view latest migration logs</Box>
                                    </Box>
                                </ListItem>}
                                {(logs || []).map((log, i) => {
                                    return (
                                        <ListItem key={'sensor-log-' + i}>
                                            <Box width="100%">
                                                <Box component="span" style={{ display: 'inline-block', width: 150 }}>
                                                    {log.timestamp.format(theme.date.long)}</Box>
                                                <Box component="span" style={{ display: 'inline-block', width: 70 }}>
                                                    {log.status}</Box>
                                                <Box component="span">
                                                    {log.msg}</Box>
                                            </Box>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    </React.Fragment> : null;
};
MigrationStatus.propTypes = {
    wantMigration: PropTypes.string,
    onMigrationStatusAck: PropTypes.func,
    onStartMigration: PropTypes.func,
};

export default Cluster;
